import { useCallback, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Form, Row, Col, Button } from 'react-bootstrap';
import LoadingSpinner from '../common/LoadingSpinner';
import { getAvailableBenefitFundTypes } from '../../utils/fundTypeUtils';
import FundTypeRenderer from './FundTypeRenderer';
import CurrentBenefitsPanel from './CurrentBenefitsPanel';
import BenefitSummaryPanelsRow from './BenefitSummaryPanelsRow';
import AgencyInfoModal from './AgencyOverrideModal';
import SubmitBenefitConfirmationModal from './SubmitBenefitConfirmationModal';
import { FUND_TYPES } from '../../constants/constants';
import { useBenefitTierCalculation } from './common/hooks/useBenefitTierCalculation';
import { useBenefitGrantCalculation } from './common/hooks/useBenefitGrantCalculation';
import { FormValidationContext } from './common/FormValidationContext';
import Required from '../../utils/Required';

const BenefitUpdateForm = ({ formData, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState({});
  const [validated, setValidated] = useState(false);

  // Modal states
  const [showAgencyInfoModal, setShowAgencyInfoModal] = useState(false);
  const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
  const [agencyOverride, setAgencyOverride] = useState(null);
  const [confirmationSnapshot, setConfirmationSnapshot] = useState(null);
  const { isAdmin } = useAuth();

  const getCleanFormForFundType = (fundType) => {
    return {
      fundType,
      otherFundType: '',
      pledgeAmount: '',
      householdSize: '',
      income: '',
      arrearsLiabilityAmount: '',
      arrearage: '',
      energyDiscount: '',
      incomeMeasurementType: '',
      incomeMeasurementPercentage: '',
      submissionType: 'NEW',
      additionalInformation: '',

      // CARES-specific
      caresType: '',
      caresExceptionRequestFlg: false,
      exceptionReason: '',
      referralSource: '',
      tpaFlg: false,
      tpaMonths: '',
      tpaMonthlyInstallmentEst: '',
      weatherizationProgramInterest: false,
    };
  };

  const handleFundTypeChange = (e) => {
    const newFundType = e.target.value;
    setForm(getCleanFormForFundType(newFundType));
    setValidated(false);
  };

  const handleCloseAgencyInfoModal = () => {
    setShowAgencyInfoModal(false);
  };

  const setFormField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const { isTierCalculationPending } = useBenefitTierCalculation({
    form,
    formData,
    setFormField,
  });

  const { isGrantCalculationPending } = useBenefitGrantCalculation({
    form,
    formData,
    setFormField,
  });

  const isCalculationPending = isTierCalculationPending || isGrantCalculationPending;
  const isCalculatedValueReady = (value) => value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value));

  const isCalculationRequiredButIncomplete = () => {
    const isWa = formData?.state === 'WA';

    // PPC / OLIBA: percentage input drives calculated arrearage + discount.
    if (form?.fundType === FUND_TYPES.PPC && form?.incomeMeasurementType && form?.incomeMeasurementPercentage) {
      return !isCalculatedValueReady(form?.arrearage) || !isCalculatedValueReady(form?.energyDiscount);
    }

    // LIHEAP (WA): household size + income drives calculated values.
    if (form?.fundType === FUND_TYPES.LIHEAP && isWa && form?.householdSize && form?.income) {
      return (
        !isCalculatedValueReady(form?.arrearage)
        || !isCalculatedValueReady(form?.energyDiscount)
        || !form?.incomeMeasurementType
        || !isCalculatedValueReady(form?.incomeMeasurementPercentage)
      );
    }

    // CARES (WA): household size + income drives calculated values.
    if (form?.fundType === FUND_TYPES.CARES && isWa && form?.householdSize && form?.income) {
      return (
        !isCalculatedValueReady(form?.arrearage)
        || !isCalculatedValueReady(form?.energyDiscount)
        || !form?.incomeMeasurementType
        || !isCalculatedValueReady(form?.incomeMeasurementPercentage)
      );
    }

    return false;
  };

  const buildSubmitPayload = (submissionForm, selectedAgencyOverride) => {
    let pledgeAmount = parseFloat(submissionForm?.pledgeAmount);
    if (submissionForm?.fundType === FUND_TYPES.OTHER && Number.isNaN(pledgeAmount)) {
      pledgeAmount = 0;
    }

    return {
      fundType: submissionForm?.fundType,
      otherFundType: submissionForm?.otherFundType,
      pledgeAmount,
      householdSize: parseInt(submissionForm?.householdSize, 10),
      income: parseFloat(submissionForm?.income),
      arrearsLiabilityAmount: parseFloat(submissionForm?.arrearsLiabilityAmount),
      arrearage: parseFloat(submissionForm?.arrearage),
      energyDiscount: parseInt(submissionForm?.energyDiscount),
      incomeMeasurementType: submissionForm?.incomeMeasurementType ? submissionForm?.incomeMeasurementType : null,
      incomeMeasurementPercentage: parseFloat(submissionForm?.incomeMeasurementPercentage),
      submissionType: submissionForm?.submissionType,
      additionalInformation: submissionForm?.additionalInformation,

      // Agency override (admin-only)
      username: selectedAgencyOverride ? selectedAgencyOverride.username : null,

      // CARES-specific
      caresType: submissionForm?.caresType ? submissionForm?.caresType : null,
      caresExceptionRequestFlg: submissionForm?.caresExceptionRequestFlg,
      exceptionReason: submissionForm?.exceptionReason,
      referralSource: submissionForm?.referralSource,
      tpaFlg: (submissionForm?.fundType === FUND_TYPES.CARES ? submissionForm?.tpaFlg === 'Yes' : null),
      tpaMonths: parseInt(submissionForm?.tpaMonths),
      tpaMonthlyInstallmentEst: parseFloat(submissionForm?.tpaMonthlyInstallmentEst),
      weatherizationProgramInterest: (submissionForm?.fundType === FUND_TYPES.CARES ? submissionForm?.weatherizationProgramInterest === 'Yes' : null),
    };
  };

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    const htmlForm = e.currentTarget;
    if (!htmlForm.checkValidity()) {
      setValidated(true);
      return;
    }

    if (isCalculationPending || isCalculationRequiredButIncomplete()) {
      setValidated(true);
      return;
    }

    setConfirmationSnapshot({
      form: { ...form },
      agencyOverride,
      payload: buildSubmitPayload(form, agencyOverride),
    });
    setShowSubmitConfirmationModal(true);
  };

  const handleCloseSubmitConfirmationModal = () => {
    setShowSubmitConfirmationModal(false);
  };

  const handleConfirmSubmit = async () => {
    if (!confirmationSnapshot?.payload) {
      return;
    }

    setShowSubmitConfirmationModal(false);
    await onSubmit(confirmationSnapshot.payload);
  };

  const resetFormForCaresType = (newCaresType) => {
    setForm(prev => ({
      ...getCleanFormForFundType(prev.fundType),
      fundType: prev.fundType,
      caresType: newCaresType,
    }));
    setValidated(false);
  };

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <FormValidationContext.Provider value={validated}>
          {/* Header */}
          <div className="card bg-secondary-soft form-section bg-muted p-3 pt-0 rounded mb-4">
            <BenefitSummaryPanelsRow
              customerProps={{
                customerFirstName: formData?.customerFirstName,
                customerLastName: formData?.customerLastName,
                accountId: formData?.accountId,
                serviceAddress: formData?.serviceAddress,
                serviceCity: formData?.serviceCity,
                serviceState: formData?.serviceState,
                serviceZip: formData?.serviceZip,
                county: formData?.county,
              }}
              currentBenefitsProps={{
                state: formData?.state,
                fundType: form?.fundType,
                currentEnergyDiscount: formData?.currentDiscountPercentage,
                numGrants: form?.fundType === FUND_TYPES.CARES ? formData?.caresPledgesApplied : null,
                totalGrantsApplied: form?.fundType === FUND_TYPES.CARES ? formData?.totalCaresGrantsApplied : null,
              }}
              agencyProps={{
                agencyName: agencyOverride?.agencyName || formData?.agencyName,
                agencyEmail: agencyOverride?.agencyEmail || formData?.agencyEmail,
                username: agencyOverride?.username || formData?.username,
              }}
              canOverrideAgency={isAdmin}
              onOverrideAgency={() => setShowAgencyInfoModal(true)}
            />
          </div>

          {/* Benefit Type */}
          <Row className="form-section mb-4">
            <h4>Benefit Type</h4>
            <Col md={6} className="mt-3">
              <Form.FloatingLabel label={<Required>Fund Type</Required>} controlId="fund-type">
                <Form.Select
                  value={form.fundType}
                  onChange={handleFundTypeChange}
                  disabled={isSubmitting}
                  required
                >
                  <option value="">Select Fund Type</option>
                  {getAvailableBenefitFundTypes(formData?.state, formData?.agencyName).map(ft => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Please select a Fund Type.
                </Form.Control.Feedback>
              </Form.FloatingLabel>
            </Col>
          </Row>

          {/* Fund-specific UI */}
          <FundTypeRenderer
            fundType={form.fundType}
            form={form}
            formData={formData}
            setFormField={setFormField}
            onCaresTypeChange={resetFormForCaresType}
            isSubmitting={isSubmitting}
          />

          {/* Submit */}
          <div className="d-grid gap-2 mt-4">
            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting || isCalculationPending || isCalculationRequiredButIncomplete()}
            >
              {isSubmitting ? (
                <LoadingSpinner inline message="Submitting…" />
              ) : (isCalculationPending || isCalculationRequiredButIncomplete()) ? (
                <LoadingSpinner inline message="Calculating benefit values..." />
              ) : (
                'Submit Benefit'
              )}
            </Button>
            {(isCalculationPending || isCalculationRequiredButIncomplete()) && (
              <Form.Text className="text-muted text-center">
                Please wait while arrearage and discount values are calculated.
              </Form.Text>
            )}
          </div>
        </FormValidationContext.Provider>
      </Form>
      
      {/* Mount Modals */}
      <AgencyInfoModal
        show={showAgencyInfoModal}
        onSubmit={(override) => {
          setAgencyOverride(override);
          setShowAgencyInfoModal(false);
          return { success: true };
        }}
        onClose={handleCloseAgencyInfoModal}
      />

      <SubmitBenefitConfirmationModal
        show={showSubmitConfirmationModal}
        onClose={handleCloseSubmitConfirmationModal}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
        formData={formData}
        formSnapshot={confirmationSnapshot?.form}
        agencyOverride={confirmationSnapshot?.agencyOverride}
        submissionPayload={confirmationSnapshot?.payload}
      />
    </>
  );
};

export default BenefitUpdateForm;
